import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { IMG, NAV } from "@/config/site";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const light = !scrolled;

  return (
    <>
      <header
        data-testid="site-header"
        className="pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center px-3 pt-3 md:px-6 md:pt-5"
      >
        <motion.div
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className={`pointer-events-auto flex w-full max-w-[1480px] items-center justify-between transition-all duration-[600ms] ease-[cubic-bezier(.22,1,.36,1)] ${
            scrolled
              ? "rounded-[1.6rem] border border-[#17251C]/10 bg-[#F3EDE2]/95 px-4 py-2.5 shadow-[0_18px_50px_-24px_rgba(23,37,28,0.45)] backdrop-blur-xl md:px-6"
              : "rounded-[2rem] border border-transparent px-4 py-4 md:px-6"
          }`}
        >
          <Link to="/" data-testid="nav-garten-link" aria-label="Hanse Garten Bremen Startseite">
            <span
              className={`flex items-center rounded-2xl transition-all duration-500 ${
                light ? "bg-[#F3EDE2]/90 px-3 py-1.5 backdrop-blur-md" : ""
              }`}
            >
              <img
                src={IMG.logo}
                alt="Hanse Garten Bremen – Gartengestaltung & Landschaftsbau"
                className={`w-auto transition-all duration-[600ms] ${scrolled ? "h-10 md:h-11" : "h-12 md:h-16"}`}
              />
            </span>
          </Link>

          <nav className="hidden items-center gap-9 lg:flex">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to === "/" ? "garten" : n.to.slice(1)}-link`}
                className={({ isActive }) =>
                  `group relative text-[0.66rem] font-bold tracking-[0.26em] transition-colors duration-400 ${
                    isActive
                      ? "text-[#B4623C]"
                      : light
                        ? "text-[#F3EDE2] hover:text-[#DCCDB4]"
                        : "text-[#2F3D2E] hover:text-[#B4623C]"
                  }`
                }
              >
                {n.label}
                <span className="absolute -bottom-2 left-0 h-[1px] w-0 bg-current transition-all duration-500 group-hover:w-full" />
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            <span className="hidden md:block">
              <Link
                to="/kontakt"
                data-testid="header-cta-consultation"
                className={`!px-5 !py-3 !text-[0.62rem] ${light ? "btn-outline on-dark" : "btn-primary"}`}
              >
                Kostenlose Beratung
                <ArrowUpRight size={14} />
              </Link>
            </span>
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menü"
              className={`flex h-12 w-12 items-center justify-center rounded-full border transition-colors duration-400 lg:hidden ${
                light
                  ? "border-[#F3EDE2]/45 bg-[#17251C]/35 text-[#F3EDE2] backdrop-blur-md"
                  : "border-[#17251C]/20 bg-[#F3EDE2]/70 text-[#17251C]"
              }`}
            >
              {open ? <X size={19} /> : <Menu size={19} />}
            </button>
          </div>
        </motion.div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ clipPath: "circle(0% at 92% 6%)" }}
            animate={{ clipPath: "circle(145% at 92% 6%)" }}
            exit={{ clipPath: "circle(0% at 92% 6%)" }}
            transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-40 overflow-y-auto bg-[#17251C] px-6 pb-16 pt-32 lg:hidden"
          >
            <p className="eyebrow !text-[#93A387]">Navigation</p>
            <nav className="mt-6 flex flex-col">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.18 + i * 0.07, duration: 0.6 }}
                >
                  <Link
                    to={n.to}
                    data-testid={`mobile-nav-${n.to === "/" ? "garten" : n.to.slice(1)}-link`}
                    className="display-xl block border-b border-[#93A387]/20 py-5 text-4xl text-[#F3EDE2]"
                  >
                    {n.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <Link
              to="/kontakt"
              data-testid="mobile-cta-consultation"
              className="btn-primary mt-10 w-full !bg-[#B4623C]"
            >
              Kostenlose Beratung
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
